import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Sparkle, EnvelopeSimple, DownloadSimple, Lock, Clock, PaperPlaneTilt, FileText } from "@phosphor-icons/react";
import { Section } from "./_shared";
import BriefDashboard from "./BriefDashboard";
import logger from "@/lib/logger";

export default function ProjectBrief({ projectId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [schedule, setSchedule] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/projects/${projectId}/brief`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load brief");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [projectId]);
  useEffect(() => { if (user?.email) setRecipientEmail(user.email); }, [user]);

  const loadSchedule = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/brief/schedule`);
      setSchedule(data);
    } catch (err) {
      // Schedule may not exist yet — 404 is expected for new projects
      if (err?.response?.status !== 404) {
        logger.warn("Brief schedule load failed:", err?.response?.data?.detail || err?.message);
      }
    }
  };
  useEffect(() => { loadSchedule(); }, [projectId]);

  const saveSchedule = async (next) => {
    setSavingSchedule(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/brief/schedule`, next);
      setSchedule((p) => ({ ...p, ...data }));
      toast.success(next.enabled ? "Auto-send enabled" : "Auto-send disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedule");
    } finally { setSavingSchedule(false); }
  };

  const dispatchNow = async () => {
    setDispatching(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/brief/dispatch-now`);
      toast.success(`Brief sent to ${data.sent_to}`);
      setSchedule((p) => ({ ...p, last_sent_at: data.last_sent_at }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed");
    } finally { setDispatching(false); }
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/projects/${projectId}/brief.pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `arissai-brief-${data?.project?.name?.replace(/\W+/g, "-") || "project"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF download failed");
    }
  };

  const sendEmail = async () => {
    if (!recipientEmail.trim()) {
      toast.error("Please enter a recipient email");
      return;
    }
    setEmailing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/brief/email`, { recipient_email: recipientEmail });
      toast.success(`Brief emailed to ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email send failed");
    } finally { setEmailing(false); }
  };

  if (error) {
    const isUpgrade = error?.toLowerCase().includes("requires");
    return (
      <div className="bg-white border border-zinc-200 p-10 text-center" data-testid="brief-error">
        <Lock size={28} weight="duotone" className="mx-auto text-zinc-400" />
        <h3 className="font-display font-bold text-2xl mt-3">{isUpgrade ? "Upgrade required" : "Brief unavailable"}</h3>
        <p className="text-zinc-600 mt-2">{error}</p>
        {isUpgrade && (
          <a href="/pricing" className="mt-5 inline-flex items-center gap-2 h-11 px-5 bg-zinc-950 text-white text-sm font-bold hover:bg-zinc-800" data-testid="brief-upgrade-cta">
            View plans <Sparkle size={14} weight="fill" />
          </a>
        )}
      </div>
    );
  }

  return (
    <div data-testid="brief-lens">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// PMO Weekly Brief</div>
          <h2 className="font-display font-black tracking-tighter text-3xl mt-1">Executive digest</h2>
          <p className="text-sm text-zinc-600 mt-1">Bundles the Insights headline, top risks, open issues, stakeholder watchlist and AI recommendations into one shareable artefact.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-300 text-xs font-bold hover:bg-zinc-100 disabled:opacity-50"
            data-testid="brief-regenerate">
            <Sparkle size={12} weight="fill" />
            {loading ? "Generating…" : "Regenerate"}
          </button>
          <button onClick={downloadPdf} disabled={!data}
            className="inline-flex items-center gap-2 h-10 px-4 bg-zinc-950 text-white text-xs font-bold hover:bg-zinc-800 disabled:opacity-50"
            data-testid="brief-download">
            <DownloadSimple size={12} weight="bold" /> Download PDF
          </button>
        </div>
      </div>

      <Section eyebrow="Share" title="Email this brief">
        <div className="flex flex-wrap gap-3 items-center">
          <input
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="recipient@company.com"
            className="flex-1 min-w-[260px] h-11 px-4 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
            data-testid="brief-email-input"
          />
          <button
            onClick={sendEmail}
            disabled={emailing}
            className="h-11 px-5 inline-flex items-center gap-2 bg-[#0020F5] text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
            data-testid="brief-send-email"
          >
            <EnvelopeSimple size={14} weight="bold" />
            {emailing ? "Sending…" : "Send via Resend"}
          </button>
        </div>
          <p className="text-xs text-zinc-500 mt-2">A PDF of the brief is attached automatically. In Resend test mode, delivery only works to verified addresses — verify a domain in your Resend dashboard for production sends.</p>
      </Section>

      <Section eyebrow="Schedule" title={<><Clock className="inline -mt-1" size={18} weight="duotone" /> Auto-deliver every Monday</>}>
        {!schedule?.can_schedule ? (
          <div className="bg-zinc-50 border border-zinc-200 p-4 text-sm text-zinc-700 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2"><Lock size={16} weight="duotone" /> Scheduled briefs require <b>Pro</b> or higher.</div>
            <a href="/pricing" className="h-9 px-4 inline-flex items-center gap-2 bg-zinc-950 text-white text-xs font-bold hover:bg-zinc-800" data-testid="schedule-upgrade-cta">Upgrade</a>
          </div>
        ) : (
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="schedule-toggle-label">
              <input
                type="checkbox"
                checked={!!schedule?.enabled}
                onChange={(e) => saveSchedule({ enabled: e.target.checked, recipient_email: schedule?.recipient_email })}
                disabled={savingSchedule}
                className="w-5 h-5 accent-zinc-950"
                data-testid="schedule-enabled-toggle"
              />
              <span className="font-display font-bold">Send a Weekly Brief automatically every Monday at 08:00 UTC</span>
            </label>

            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Recipient email</label>
                <input
                  type="email"
                  value={schedule?.recipient_email || ""}
                  onChange={(e) => setSchedule((p) => ({ ...p, recipient_email: e.target.value }))}
                  onBlur={(e) => saveSchedule({ enabled: !!schedule?.enabled, recipient_email: e.target.value })}
                  placeholder={user?.email || "pmo@company.com"}
                  className="mt-2 w-full h-11 px-4 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
                  data-testid="schedule-recipient-input"
                />
              </div>
              <button
                onClick={dispatchNow}
                disabled={dispatching || !schedule?.enabled}
                className="h-11 px-5 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-bold hover:bg-zinc-800 disabled:opacity-50"
                data-testid="schedule-dispatch-now"
              >
                <PaperPlaneTilt size={14} weight="bold" />
                {dispatching ? "Sending…" : "Send now"}
              </button>
            </div>

            <div className="text-xs text-zinc-500 flex flex-wrap items-center gap-4">
              <span>Window: <span className="font-mono">{schedule?.next_window}</span></span>
              {schedule?.last_sent_at && <span>Last sent: <span className="font-mono">{new Date(schedule.last_sent_at).toLocaleString()}</span></span>}
            </div>
          </div>
        )}
      </Section>

      {loading && <div className="text-zinc-500 text-sm">Generating brief…</div>}

      {/* Power-BI–style dashboard view — primary */}
      {data && <BriefDashboard data={data} />}

      {/* Static HTML preview — collapsed below; mirrors what is emailed/attached */}
      {data?.html && (
        <details className="mt-6 bg-white border border-zinc-200" data-testid="brief-html-toggle">
          <summary className="cursor-pointer px-5 py-3 font-display font-bold text-sm border-b border-zinc-200 inline-flex items-center gap-2 select-none">
            <FileText size={14} weight="duotone" /> View static HTML (what gets emailed)
          </summary>
          <iframe
            title="ARISSAI Weekly Brief"
            srcDoc={data.html}
            className="w-full"
            style={{ minHeight: "700px", border: 0 }}
            data-testid="brief-preview"
          />
        </details>
      )}
    </div>
  );
}
